/**
 * Capability API client.
 *
 * The shape to notice: `listCapabilities` returns no link, and cannot. The server holds only the
 * SHA-256 verifier, so there is nothing to send even if a route wanted to send it — the guarantee
 * is structural rather than a matter of the client behaving.
 *
 * A raw link arrives **only** from `issueCapability`, `regenerate` and `replaceShortLink`, once
 * each. Nothing here caches one, and nothing writes one to storage.
 */

import { request } from '../../api/client';

export type CapabilityKind = 'participant' | 'administrator';

export interface CapabilityRecord {
  id: string;
  kind: CapabilityKind;
  label: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  superseded_at: string | null;
  usable: boolean;
  /** `active` | `revoked` | `expired` | `superseded` — decided server-side. */
  state: string;
  last_used_at: string | null;
  issued_by_account: boolean;
}

export interface IssuedLink {
  id?: string;
  kind?: CapabilityKind;
  url?: string;
  short_url?: string;
  expires_at?: string | null;
  shown_once: boolean;
}

export function fetchCapabilities(liturgyId: string): Promise<{ data: CapabilityRecord[] }> {
  return request(`/liturgies/${liturgyId}/capabilities`);
}

export function issueCapability(
  liturgyId: string,
  input: { kind: CapabilityKind; label: string | null; expires_at: string | null },
): Promise<IssuedLink> {
  return request(`/liturgies/${liturgyId}/capabilities`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Terminal. Every link to this grant stops working. */
export function revokeCapability(capabilityId: string): Promise<void> {
  return request(`/capabilities/${capabilityId}/revoke`, { method: 'POST' });
}

/** A new credential for the same grant — old links die, the history survives. */
export function regenerateCapability(capabilityId: string): Promise<IssuedLink> {
  return request(`/capabilities/${capabilityId}/regenerate`, { method: 'POST' });
}

/** Only the short address changes. The canonical link keeps working. */
export function replaceShortLink(capabilityId: string): Promise<IssuedLink> {
  return request(`/capabilities/${capabilityId}/replace-short-link`, { method: 'POST' });
}

export const KIND_COPY: Record<CapabilityKind, { label: string; note: string }> = {
  participant: {
    label: 'Participant',
    note: 'May make and send selections. Cannot approve, cannot see who else has access, cannot share further.',
  },
  administrator: {
    label: 'Administrator',
    note: 'May review, approve and finalize, and may share further access. Everything except transferring ownership.',
  },
};

/** Why a link stopped working, in words. */
export const STATE_COPY: Record<string, string> = {
  active: 'Working',
  revoked: 'Withdrawn',
  expired: 'Expired',
  superseded: 'Replaced',
};
